import { useMemo } from "react";
import { useTranslation } from "react-i18next";

const AXES = [
  { key: "max_size_mm", labelKey: "quoteTabs.capacity", refMax: 2000, unit: "mm" },
  { key: "spindle_kw", labelKey: "quoteTabs.spindle", refMax: 45, unit: "kW" },
  { key: "max_torque_nm", labelKey: "quoteTabs.torque", refMax: 800, unit: "Nm" },
  { key: "max_rpm", labelKey: null, label: "RPM", refMax: 24000, unit: "rpm" },
  { key: "roughing_capacity_cm3_min", labelKey: "quoteTabs.roughing", refMax: 400, unit: "cm³/min" },
];

const COLORS = [
  { stroke: "#34d399", fill: "rgba(52,211,153,0.18)", dot: "#34d399" },   // emerald
  { stroke: "#22d3ee", fill: "rgba(34,211,238,0.15)", dot: "#22d3ee" },   // cyan
  { stroke: "#fbbf24", fill: "rgba(251,191,36,0.15)", dot: "#fbbf24" },   // amber
];

export default function MachineRadarChart({ machines = [] }) {
  const { t } = useTranslation();
  const polygons = useMemo(() => {
    if (!machines || machines.length === 0) return [];
    return machines.slice(0, 3).map((m) => {
      const points = AXES.map((ax) => {
        const raw = Number(m?.[ax.key] || 0);
        const norm = Math.max(0, Math.min(1, raw / ax.refMax));
        return norm;
      });
      return { machine: m, points };
    });
  }, [machines]);

  if (polygons.length === 0) return null;

  // Geometry
  const size = 260;
  const cx = size / 2;
  const cy = size / 2;
  const r = 92;
  const angleFor = (i) => -Math.PI / 2 + (i * 2 * Math.PI) / AXES.length;
  const toXY = (i, norm) => [cx + r * norm * Math.cos(angleFor(i)), cy + r * norm * Math.sin(angleFor(i))];
  const ringPath = (norm) =>
    AXES.map((_, i) => {
      const [x, y] = toXY(i, norm);
      return `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
    }).join(" ") + " Z";

  const machineLabel = (m) => {
    const name = (m?.machine_name || "").replace(/\s*\(.*\)$/, "").trim();
    return name.length > 28 ? name.slice(0, 26) + "…" : name;
  };
  const axisLabel = (ax) => (ax.labelKey ? t(ax.labelKey) : ax.label);

  return (
    <div className="tech-card p-5" data-testid="machine-radar-chart">
      <div className="flex items-center justify-between mb-3">
        <div className="label-tech text-blue-400">{t("radarChart.title")}</div>
        <div className="text-[10px] font-mono text-slate-500 uppercase tracking-wider">5-axis radar</div>
      </div>

      <div className="flex flex-col md:flex-row items-center gap-4">
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="shrink-0">
          {/* Concentric grid rings */}
          {[0.25, 0.5, 0.75, 1].map((n) => (
            <path key={`grid-${n}`} d={ringPath(n)} fill="none" stroke="#334155" strokeWidth={n === 1 ? 1 : 0.5} strokeDasharray={n === 1 ? "0" : "2 3"} />
          ))}
          {/* Axis lines */}
          {AXES.map((ax, i) => {
            const [x, y] = toXY(i, 1);
            return <line key={`ax-${ax.key}`} x1={cx} y1={cy} x2={x} y2={y} stroke="#334155" strokeWidth={0.5}/>;
          })}
          {/* Axis labels */}
          {AXES.map((ax, i) => {
            const [x, y] = toXY(i, 1.18);
            const anchor = Math.abs(Math.cos(angleFor(i))) < 0.3 ? "middle" : Math.cos(angleFor(i)) > 0 ? "start" : "end";
            return (
              <text key={`lbl-${ax.key}`} x={x} y={y} fontSize="9" fill="#94a3b8" textAnchor={anchor} dominantBaseline="middle" fontFamily="ui-monospace, monospace">
                {axisLabel(ax)}
              </text>
            );
          })}
          {/* Machine polygons (rendered in reverse so first is on top visually) */}
          {polygons.slice().reverse().map((poly, idxRev) => {
            const idx = polygons.length - 1 - idxRev;
            const color = COLORS[idx % COLORS.length];
            const pathD = poly.points
              .map((norm, i) => {
                const [x, y] = toXY(i, norm);
                return `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
              })
              .join(" ") + " Z";
            return (
              <g key={`poly-${idx}`}>
                <path d={pathD} fill={color.fill} stroke={color.stroke} strokeWidth={1.5}/>
                {poly.points.map((norm, i) => {
                  const [x, y] = toXY(i, norm);
                  return <circle key={`dot-${idx}-${i}`} cx={x} cy={y} r={2.5} fill={color.dot}/>;
                })}
              </g>
            );
          })}
        </svg>

        {/* Legend */}
        <div className="flex-1 min-w-0 space-y-2 text-xs w-full">
          {polygons.map((poly, idx) => {
            const color = COLORS[idx % COLORS.length];
            return (
              <div key={`leg-${idx}`} className="flex items-start gap-2" data-testid={`radar-legend-${idx}`}>
                <span className="w-2.5 h-2.5 rounded-sm shrink-0 mt-1" style={{ background: color.stroke }}/>
                <div className="min-w-0">
                  <div className="text-slate-200 font-medium truncate">{machineLabel(poly.machine)}</div>
                  <div className="text-[10px] text-slate-500 font-mono-data">
                    {poly.machine?.spindle_kw ?? "?"} kW · {poly.machine?.max_torque_nm ?? "?"} Nm · {poly.machine?.max_size_mm ?? "?"} mm
                  </div>
                </div>
              </div>
            );
          })}
          <div className="pt-2 mt-2 border-t border-slate-800 text-[10px] text-slate-500 leading-relaxed">
            {t("radarChart.refNote")}
          </div>
        </div>
      </div>
    </div>
  );
}
